import { Icon } from "@/components/ui/Icon";
import { MemoMarkdown } from "@/components/memo/MemoMarkdown";
import { formatLogDate } from "@/lib/format";
import { t } from "@/lib/strings";
import type { MemoEntry } from "@/lib/types";

interface Props {
  /** A `kind === "update"` entry: board activity materialised under its to-do. */
  entry: MemoEntry;
  /** Who wrote it on the board (resolved from `entry.created_by` by the caller). */
  authorName: string;
  canEdit: boolean;
  onDelete: (entryId: string) => void;
}

/**
 * One board-sourced update line, hanging under the to-do it reports on: a muted,
 * indented `⤷ 09.07.2026 · MKO` above the comment's body.
 *
 * 🔴 **Read-only by design — no click-to-edit and no editor.** The line names the
 * person who wrote it on the board; letting anyone else rewrite it would make that
 * attribution a lie. A hover-only DELETE (gated on `canEdit`) is the sole affordance,
 * and it is the whole safety story: nothing filters which board comments are
 * "protocol-worthy", so the minute-taker curates — and a removed line stays removed
 * (the backend never re-scans; `source_ref` is written once).
 *
 * The body is a comment, so it is markdown and may carry images and @-mentions —
 * rendered in FULL through `MarkdownContent` (XSS-safe via rehype-sanitize). It is
 * deliberately not excerpted: this is the document, not a preview.
 */
export function MemoUpdateRow({ entry, authorName, canEdit, onDelete }: Props) {
  return (
    <div
      // Same hover class as a point, so the delete button reveals on hover only
      // (.flow-memo-entry:hover .flow-memo-actions in index.css).
      className="flow-memo-entry"
      id={`memo-entry-${entry.id}`}
      style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "2px 0 4px" }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 12,
          marginTop: 2,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--ink-4)",
        }}
      >
        ⤷
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: "0.01em",
            color: "var(--ink-4)",
          }}
        >
          {entry.log_date ? <span>{formatLogDate(entry.log_date)}</span> : null}
          {entry.log_date ? <span aria-hidden>·</span> : null}
          <span>{authorName}</span>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
          <MemoMarkdown markdown={entry.body} />
        </div>
      </div>

      {canEdit ? (
        <div className="flow-memo-actions" style={{ display: "flex", flexShrink: 0 }}>
          <button
            type="button"
            title={t.memoUpdateRemove}
            aria-label={t.memoUpdateRemove}
            onClick={() => onDelete(entry.id)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 3,
              lineHeight: 0,
            }}
          >
            <Icon name="trash" size={13} color="var(--ink-4)" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
